import React from 'react';
import { Calendar, Clock, FileText, MoreVertical } from 'lucide-react';
import PatientAvatar from '../../WorkListTable/components/PatientAvatar';
import { buildPatientDisplayName } from '../../../shared/utils/nameUtils';

const Icons = { Calendar, Clock, FileText, MoreVertical } as any;

interface StudiesLayoutProps {
    data: any[];
    loading?: boolean;
    startIndex?: number;
    onRowClick?: (row: any) => void;
    renderActions?: (row: any) => React.ReactNode;
    renderShare?: (row: any) => React.ReactNode;
    formatDate?: (row: any) => string;
    scale?: number;
}

const StudiesLayout: React.FC<StudiesLayoutProps> = ({
    data,
    loading = false,
    startIndex = 0,
    onRowClick,
    renderActions,
    renderShare,
    formatDate,
    scale = 1,
}) => {

    if (loading) {
        return <StudiesSkeleton />;
    }

    if (!data || data.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
                <div className="w-24 h-24 mb-6 rounded-full flex items-center justify-center bg-[var(--v2-bg-card-inner)]">
                    <div className="w-12 h-12 text-[var(--v2-text-muted)]">
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                    </div>
                </div>
                <h3 className="text-xl font-bold mb-2 text-[var(--v2-text-heading)]">No studies found</h3>
                <p className="max-w-md mx-auto text-[15px] text-[var(--v2-text-secondary)]">
                    No studies match your search or date range. Try adjusting your filters or upload a new study.
                </p>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-3 pb-4">
            {/* Desktop Header */}
            <div className="hidden lg:block" style={{ zoom: scale }}>
                <div className="depth-table-header grid grid-cols-[50px_50px_minmax(140px,1.5fr)_minmax(130px,1.3fr)_minmax(150px,1.5fr)_minmax(120px,1.2fr)_50px_70px] gap-3 px-6 py-3.5 text-[14px] font-bold uppercase tracking-wider whitespace-nowrap">
                    <div>&nbsp;</div> {/* Avatar */}
                    <div>S.No</div>
                    <div>Patient Name</div>
                    <div>Date / Time</div>
                    <div>Description</div>
                    <div>Accession No.</div>
                    {/* <div className="text-center">Share</div> */}
                    <div className="text-center">Actions</div>
                </div>
            </div>

            {/* Desktop Rows */}
            <div className="hidden lg:flex lg:flex-col lg:gap-3" style={{ zoom: scale }}>
                {data.map((row, index) => (
                    <div
                        key={row.StudyInstanceUID || row.SK || index}
                        onClick={() => onRowClick?.(row)}
                        className="group relative rounded-xl cursor-pointer transition-all duration-200 bg-[var(--v2-bg-card)] border border-[var(--v2-border-card)] shadow-[var(--v2-shadow-card)] hover:shadow-[var(--v2-shadow-card-hover)] hover:border-[var(--v2-border-glass-strong)]"
                    >
                        <div className="p-4 grid grid-cols-[50px_50px_minmax(140px,1.5fr)_minmax(130px,1.3fr)_minmax(150px,1.5fr)_minmax(120px,1.2fr)_50px_70px] gap-3 items-center">
                            {/* Avatar */}
                            <div>
                                <PatientAvatar name={buildPatientDisplayName(row)} mrn={row.SK} />
                            </div>

                            {/* S.No */}
                            <div>
                                <span className="text-[15px] font-bold text-[var(--v2-text-muted)]">
                                    {startIndex + index + 1}
                                </span>
                            </div>

                            {/* Patient Name */}
                            <div className="overflow-hidden">
                                <h4 className="font-bold text-[15px] truncate whitespace-nowrap text-[var(--v2-text-primary)]">
                                    {buildPatientDisplayName(row) || 'Unknown Patient'}
                                </h4>
                                {/* <span className="text-[14px] whitespace-nowrap text-[var(--v2-text-muted)]">
                                    {row.Modality || '—'}
                                </span> */}
                            </div>

                            {/* Date / Time */}
                            <div>
                                <span className="text-[15px] flex items-center gap-1 text-[var(--v2-text-primary)]">
                                    <Icons.Calendar className="w-3.5 h-3.5 text-[var(--v2-text-muted)]" />
                                    {formatDate ? formatDate(row) : row.StudyDate || '—'}
                                </span>
                            </div>

                            {/* Description */}
                            <div className="overflow-hidden">
                                <span className="text-[15px] truncate block text-[var(--v2-text-primary)]" title={row.StudyDescription}>
                                    {row.StudyDescription || '—'}
                                </span>
                            </div>

                            {/* Accession Number */}
                            <div>
                                <span className="text-[15px] font-medium text-[var(--v2-text-secondary)]">
                                    {row.AccessionNumber || '—'}
                                </span>
                            </div>

                            {/* Share */}
                            {/* <div className="flex items-center justify-center" onClick={e => e.stopPropagation()}>
                                {renderShare?.(row)}
                            </div> */}

                            {/* Actions */}
                            <div className="flex items-center justify-center" onClick={e => e.stopPropagation()}>
                                {renderActions?.(row)}
                            </div>
                        </div>

                        {/* Left indicator bar */}
                        <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-xl ${row.Modality === 'CT' ? 'bg-blue-500' :
                            row.Modality === 'MR' || row.Modality === 'MRI' ? 'bg-emerald-500' :
                                row.Modality === 'US' ? 'bg-purple-500' :
                                    'bg-transparent'
                            }`} />
                    </div>
                ))}
            </div>

            {/* Mobile Card Layout — Slim compact cards */}
            <div className="lg:hidden flex flex-col gap-3 px-0.5">
                {data.map((row, index) => {
                    const accentColor = row.Modality === 'CT' ? '#3b82f6' :
                        row.Modality === 'MR' || row.Modality === 'MRI' ? '#10b981' :
                            row.Modality === 'US' ? '#a855f7' :
                                '#818cf8';

                    return (
                        <div
                            key={row.StudyInstanceUID || row.SK || index}
                            onClick={() => onRowClick?.(row)}
                            className="relative rounded-xl cursor-pointer overflow-hidden transition-all duration-200 active:scale-[0.99]"
                            style={{
                                background: 'var(--v2-bg-card)',
                                border: '1px solid var(--v2-border-card)',
                                borderLeft: `3px solid ${accentColor}`,
                                boxShadow: '0 2px 8px -2px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.05)',
                            }}
                        >
                            {/* Row 1: Avatar + Name + Modality Pill */}
                            <div className="flex items-center justify-between gap-3 px-4 pt-3.5 pb-2.5">
                                <div className="flex items-center gap-2.5 min-w-0">
                                    <div
                                        className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center text-[13px] font-black text-white"
                                        style={{ background: accentColor }}
                                    >
                                        {buildPatientDisplayName(row) ? buildPatientDisplayName(row).charAt(0).toUpperCase() : '?'}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-bold text-[14px] leading-tight truncate" style={{ color: 'var(--v2-text-primary)' }}>
                                            {buildPatientDisplayName(row) || 'Unknown Patient'}
                                        </p>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            {row.Modality && (
                                                <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider text-white" style={{ background: accentColor }}>
                                                    {row.Modality}
                                                </span>
                                            )}
                                            <span className="text-[11px] font-medium" style={{ color: 'var(--v2-text-muted)' }}>
                                                Acc: {row.AccessionNumber || 'N/A'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Row 2: Description */}
                            <div
                                className="px-4 py-2"
                                style={{ borderTop: '1px solid var(--v2-border-subtle)' }}
                            >
                                <p className="text-[12px] font-medium truncate" style={{ color: 'var(--v2-text-secondary)' }}>
                                    {row.StudyDescription || 'No description provided'}
                                </p>
                            </div>

                            {/* Row 3: Date & Actions */}
                            <div
                                className="flex items-center justify-between gap-2 px-4 py-2.5"
                                style={{ borderTop: '1px solid var(--v2-border-subtle)', background: 'var(--v2-bg-card-inner)' }}
                            >
                                <div className="flex items-center gap-1.5 min-w-0">
                                    <Icons.Calendar className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--v2-text-muted)' }} />
                                    <span className="text-[11px] font-medium truncate" style={{ color: 'var(--v2-text-primary)' }}>
                                        {formatDate ? formatDate(row) : row.StudyDate || '—'}
                                    </span>
                                </div>

                                <div className="flex items-center justify-end flex-shrink-0 gap-1" onClick={e => e.stopPropagation()}>
                                    {renderShare?.(row)}
                                    {renderActions?.(row)}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

/* ─── Skeleton ─── */
const StudiesSkeleton = () => (
    <div className="flex flex-col gap-3 pb-4">
        {/* Header */}
        <div className="hidden lg:block rounded-xl overflow-hidden mb-1" style={{ border: '1px solid var(--v2-border-card)', background: 'var(--v2-table-header-bg)' }}>
            <div className="depth-table-header p-3 grid grid-cols-[50px_50px_minmax(140px,1.5fr)_minmax(130px,1.3fr)_minmax(150px,1.5fr)_minmax(120px,1.2fr)_50px_70px] gap-3 items-center">
                <div />
                <div>S.NO</div>
                <div>PATIENT NAME</div>
                <div>DATE / TIME</div>
                <div>DESCRIPTION</div>
                <div>ACCESSION NO.</div>
                <div className="text-center">SHARE</div>
                <div className="text-center">ACTIONS</div>
            </div>
        </div>

        {/* Rows */}
        {[...Array(6)].map((_, i) => (
            <div
                key={i}
                className="rounded-xl border border-l-4 border-l-transparent shadow-sm overflow-hidden"
                style={{ minHeight: '80px', background: 'var(--v2-bg-card)', borderColor: 'var(--v2-border-subtle)' }}
            >
                <div className="p-4 hidden lg:grid lg:grid-cols-[50px_50px_minmax(140px,1.5fr)_minmax(130px,1.3fr)_minmax(150px,1.5fr)_minmax(120px,1.2fr)_50px_70px] lg:gap-3 lg:items-center">
                    {/* Avatar */}
                    <div className="relative overflow-hidden rounded-full w-10 h-10" style={{ backgroundColor: 'var(--v2-skeleton-base)' }}>
                        <div className="absolute inset-0 -translate-x-full animate-shimmer" style={{ backgroundImage: 'linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)' }} />
                    </div>
                    {/* S.No */}
                    <div className="relative overflow-hidden rounded h-4 w-6" style={{ backgroundColor: 'var(--v2-skeleton-base)' }}>
                        <div className="absolute inset-0 -translate-x-full animate-shimmer" style={{ backgroundImage: 'linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)' }} />
                    </div>
                    {/* Patient Name */}
                    <div className="space-y-2">
                        <div className="relative overflow-hidden rounded h-4 w-3/4" style={{ backgroundColor: 'var(--v2-skeleton-base)' }}>
                            <div className="absolute inset-0 -translate-x-full animate-shimmer" style={{ backgroundImage: 'linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)' }} />
                        </div>
                        <div className="relative overflow-hidden rounded h-3 w-1/2" style={{ backgroundColor: 'var(--v2-skeleton-base)' }}>
                            <div className="absolute inset-0 -translate-x-full animate-shimmer" style={{ backgroundImage: 'linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)' }} />
                        </div>
                    </div>
                    {/* Date */}
                    <div className="relative overflow-hidden rounded h-3 w-24" style={{ backgroundColor: 'var(--v2-skeleton-base)' }}>
                        <div className="absolute inset-0 -translate-x-full animate-shimmer" style={{ backgroundImage: 'linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)' }} />
                    </div>
                    {/* Description */}
                    <div className="relative overflow-hidden rounded h-3 w-28" style={{ backgroundColor: 'var(--v2-skeleton-base)' }}>
                        <div className="absolute inset-0 -translate-x-full animate-shimmer" style={{ backgroundImage: 'linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)' }} />
                    </div>
                    {/* Accession */}
                    <div className="relative overflow-hidden rounded h-3 w-20" style={{ backgroundColor: 'var(--v2-skeleton-base)' }}>
                        <div className="absolute inset-0 -translate-x-full animate-shimmer" style={{ backgroundImage: 'linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)' }} />
                    </div>
                    {/* Share */}
                    <div className="flex justify-center">
                        <div className="relative overflow-hidden rounded-lg w-8 h-8" style={{ backgroundColor: 'var(--v2-skeleton-base)' }}>
                            <div className="absolute inset-0 -translate-x-full animate-shimmer" style={{ backgroundImage: 'linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)' }} />
                        </div>
                    </div>
                    {/* Actions */}
                    <div className="flex justify-center">
                        <div className="relative overflow-hidden rounded-lg w-8 h-8" style={{ backgroundColor: 'var(--v2-skeleton-base)' }}>
                            <div className="absolute inset-0 -translate-x-full animate-shimmer" style={{ backgroundImage: 'linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)' }} />
                        </div>
                    </div>
                </div>

                {/* Mobile skeleton */}
                <div className="p-4 lg:hidden space-y-3">
                    <div className="flex items-center gap-3">
                        <div className="relative overflow-hidden rounded-full w-10 h-10" style={{ backgroundColor: 'var(--v2-skeleton-base)' }}>
                            <div className="absolute inset-0 -translate-x-full animate-shimmer" style={{ backgroundImage: 'linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)' }} />
                        </div>
                        <div className="flex-1 space-y-2">
                            <div className="relative overflow-hidden rounded h-4 w-3/4" style={{ backgroundColor: 'var(--v2-skeleton-base)' }}>
                                <div className="absolute inset-0 -translate-x-full animate-shimmer" style={{ backgroundImage: 'linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)' }} />
                            </div>
                            <div className="relative overflow-hidden rounded h-3 w-1/2" style={{ backgroundColor: 'var(--v2-skeleton-base)' }}>
                                <div className="absolute inset-0 -translate-x-full animate-shimmer" style={{ backgroundImage: 'linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)' }} />
                            </div>
                        </div>
                    </div>
                    <div className="space-y-2">
                        <div className="relative overflow-hidden rounded h-3 w-32" style={{ backgroundColor: 'var(--v2-skeleton-base)' }}>
                            <div className="absolute inset-0 -translate-x-full animate-shimmer" style={{ backgroundImage: 'linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)' }} />
                        </div>
                        <div className="relative overflow-hidden rounded h-3 w-24" style={{ backgroundColor: 'var(--v2-skeleton-base)' }}>
                            <div className="absolute inset-0 -translate-x-full animate-shimmer" style={{ backgroundImage: 'linear-gradient(90deg, transparent 0%, var(--v2-skeleton-shimmer) 45%, var(--v2-skeleton-shimmer) 55%, transparent 100%)' }} />
                        </div>
                    </div>
                </div>
            </div>
        ))}
    </div>
);

export default StudiesLayout;
